import { DEFAULT_TIMEZONE } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { useMemo } from "react";
import { useWorkspaceDetails } from "../../../session/workspace-context";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const SECOND_MS = 1000;

export interface BuildFormatters {
  timeZone: string;
  time: (iso: string | null | undefined) => string;
  dateTime: (iso: string | null | undefined) => string;
  date: (iso: string | null | undefined) => string;
  /** "just now", "5 minutes ago", "2 hours ago", "Yesterday", then a short date. */
  ago: (iso: string | null | undefined, now?: number) => string;
  number: (value: number) => string;
  percent: (ratio: number | null | undefined, digits?: number) => string;
  /** 640 → "640 ms", 2100 → "2.1 s". */
  duration: (ms: number | null | undefined) => string;
}

/** Workspace-TZ formatters (CLAUDE.md rule 15). */
export function useBuildFormatters(): BuildFormatters {
  const { t, i18n } = useTranslation();
  const workspace = useWorkspaceDetails();
  const timeZone = workspace.data?.timezone ?? DEFAULT_TIMEZONE;
  const locale = i18n.language === "fil" ? "fil-PH" : "en-PH";
  return useMemo(() => {
    const time = new Intl.DateTimeFormat(locale, { timeZone, hour: "numeric", minute: "2-digit" });
    const date = new Intl.DateTimeFormat(locale, { timeZone, month: "short", day: "numeric", year: "numeric" });
    const dateTime = new Intl.DateTimeFormat(locale, { timeZone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    const numbers = new Intl.NumberFormat(locale);
    const safe = (fmt: Intl.DateTimeFormat) => (iso: string | null | undefined) => (iso ? fmt.format(new Date(iso)) : "");
    return {
      timeZone,
      time: safe(time),
      dateTime: safe(dateTime),
      date: safe(date),
      ago: (iso, now = Date.now()) => {
        if (!iso) return "";
        const diff = Math.max(now - new Date(iso).getTime(), 0);
        if (diff < MINUTE_MS) return t("build.time.now");
        if (diff < HOUR_MS) return t("build.time.minutes", { count: Math.floor(diff / MINUTE_MS) });
        if (diff < DAY_MS) return t("build.time.hours", { count: Math.floor(diff / HOUR_MS) });
        if (diff < 2 * DAY_MS) return t("build.time.yesterday");
        return dateTime.format(new Date(iso));
      },
      number: (value) => numbers.format(value),
      percent: (ratio, digits = 0) => (ratio === null || ratio === undefined ? "—" : `${(ratio * 100).toFixed(digits)}%`),
      duration: (ms) => {
        if (ms === null || ms === undefined) return "—";
        return ms < SECOND_MS ? `${numbers.format(Math.round(ms))} ms` : `${(ms / SECOND_MS).toFixed(1)} s`;
      },
    };
  }, [locale, timeZone, t]);
}
